import { adminApi } from '../../api/client';
import type { UserDetailsState } from './useUserDetailsState';

interface HandlerDependencies {
  userId: string | undefined;
  state: UserDetailsState;
}

export const createUserDetailsHandlers = ({ userId, state }: HandlerDependencies) => {
  const fetchUserDetails = async () => {
    if (!userId) return;

    try {
      state.setLoading(true);
      state.setError(null);

      const response = await adminApi.getUserDetails(userId);
      state.setUserDetails(response.data);
    } catch (err: any) {
      console.error('Error fetching user details:', err);
      state.setError(err.message || 'Failed to fetch user details');
    } finally {
      state.setLoading(false);
    }
  };

  const handleOpenOverrideForm = async () => {
    if (!userId) return;
    try {
      const response = await adminApi.getUserOverrides(userId);
      state.setOverrideMaxSites(response.data.overrideMaxSites?.toString() ?? '');
      state.setOverrideMaxPagesPerSite(response.data.overrideMaxPagesPerSite?.toString() ?? '');
      state.setHasActiveSubscription(response.data.hasActiveSubscription);
      state.setBillingPeriod(response.data.billingPeriod);
      // Coerce in case API returns a PostgreSQL NUMERIC string
      const planPrice = response.data.currentPlanPrice;
      const parsedPrice = planPrice == null ? null : Number(planPrice);
      state.setCurrentPlanPrice(
        parsedPrice != null && Number.isFinite(parsedPrice) ? parsedPrice : null
      );
      state.setHasCustomPrice(response.data.hasCustomPrice);
      state.setPlanName(response.data.planName);
      state.setOverridePrice('');
      state.setShowOverrideForm(true);
      state.setOverrideError(null);
      state.setOverrideSuccess(null);
    } catch (err: any) {
      console.error('Error fetching user overrides:', err);
      state.setOverrideError('Failed to load current overrides');
    }
  };

  const handleSaveOverrides = async () => {
    if (!userId) return;

    state.setSavingOverrides(true);
    state.setOverrideError(null);
    state.setOverrideSuccess(null);

    try {
      const priceCents =
        state.overridePrice === '' ? undefined : Math.round(parseFloat(state.overridePrice) * 100);

      const overrides: {
        overrideMaxSites: number | null;
        overrideMaxPagesPerSite: number | null;
        overridePrice?: number | null;
      } = {
        overrideMaxSites:
          state.overrideMaxSites === '' ? null : parseInt(state.overrideMaxSites, 10),
        overrideMaxPagesPerSite:
          state.overrideMaxPagesPerSite === '' ? null : parseInt(state.overrideMaxPagesPerSite, 10),
      };

      if (state.hasActiveSubscription && priceCents !== undefined) {
        overrides.overridePrice = priceCents;
      }

      if (
        overrides.overrideMaxSites !== null &&
        (isNaN(overrides.overrideMaxSites) || overrides.overrideMaxSites < 0)
      ) {
        state.setOverrideError('Max sites must be a positive number or empty');
        state.setSavingOverrides(false);
        return;
      }
      if (
        overrides.overrideMaxPagesPerSite !== null &&
        (isNaN(overrides.overrideMaxPagesPerSite) || overrides.overrideMaxPagesPerSite < 0)
      ) {
        state.setOverrideError('Max pages per site must be a positive number or empty');
        state.setSavingOverrides(false);
        return;
      }

      if (
        overrides.overridePrice !== undefined &&
        overrides.overridePrice !== null &&
        (isNaN(overrides.overridePrice) || overrides.overridePrice < 0)
      ) {
        state.setOverrideError('Price must be a positive number or empty');
        state.setSavingOverrides(false);
        return;
      }

      const response = await adminApi.updateUserOverrides(userId, overrides);

      if (response.priceUpdateError) {
        state.setOverrideError(response.priceUpdateError);
        state.setOverrideSuccess('Limit overrides saved, but price update failed');
      } else {
        state.setOverrideSuccess(response.message || 'Overrides saved successfully');
      }

      await fetchUserDetails();

      if (!response.priceUpdateError) {
        setTimeout(() => {
          state.setShowOverrideForm(false);
          state.setOverrideSuccess(null);
        }, 1500);
      }
    } catch (err: any) {
      console.error('Error saving overrides:', err);
      state.setOverrideError(err.message || 'Failed to save overrides');
    } finally {
      state.setSavingOverrides(false);
    }
  };

  const handleClearOverrides = async () => {
    state.setOverrideMaxSites('');
    state.setOverrideMaxPagesPerSite('');
    state.setOverridePrice('');
  };

  return {
    fetchUserDetails,
    handleOpenOverrideForm,
    handleSaveOverrides,
    handleClearOverrides,
  };
};
